import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Save, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";

/**
 * Floating pill rendered top-right. Hidden when nothing is dirty.
 * Clicking expands a list of every dirty entry; clicking a row navigates
 * to the location the entry was registered with.
 */
function timeAgo(ts) {
  if (!ts) return "just now";
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 30) return "just now";
  if (sec < 90) return "1 min ago";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default function UnsavedChangesIndicator() {
  const { entries } = useUnsavedChanges();
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  // Click outside to collapse
  useEffect(() => {
    if (!expanded) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [expanded]);

  if (entries.length === 0) return null;

  const newest = entries[0];

  const goTo = (entry) => {
    setExpanded(false);
    if (typeof entry.location === "function") {
      entry.location();
    } else if (typeof entry.location === "string" && entry.location) {
      navigate(entry.location);
    }
  };

  return (
    <div
      ref={ref}
      className="fixed top-4 right-4 z-[55]"
      data-testid="unsaved-changes-indicator"
    >
      <button
        onClick={() => setExpanded(v => !v)}
        className="bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-full px-3 py-1.5 shadow-md flex items-center gap-2 text-xs font-semibold text-amber-900 transition-all"
        data-testid="unsaved-indicator-pill"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
        <Save className="w-3 h-3" />
        {entries.length === 1 ? (
          <>
            <span className="max-w-[180px] truncate">{newest.label}</span>
            <span className="text-amber-700/70">· {timeAgo(newest.lastEditedAt)}</span>
          </>
        ) : (
          <>
            <span>{entries.length} unsaved</span>
            <span className="text-amber-700/70">· {timeAgo(newest.lastEditedAt)}</span>
          </>
        )}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div
          className="mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 w-[340px] overflow-hidden"
          data-testid="unsaved-indicator-dropdown"
        >
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
              Unsaved work
            </p>
            <p className="text-[12px] text-slate-700">
              {entries.length} place{entries.length === 1 ? "" : "s"} with edits not yet saved
            </p>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
            {entries.map(e => (
              <button
                key={e.key}
                onClick={() => goTo(e)}
                className="w-full text-left px-4 py-3 hover:bg-amber-50/60 flex items-center gap-3 transition-colors"
                data-testid={`unsaved-row-${e.key}`}
              >
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <Save className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-slate-800 truncate">{e.label}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <Clock className="w-2.5 h-2.5" /> Last edited {timeAgo(e.lastEditedAt)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
